import { useMemo, useRef, useState } from 'react';
import { formatValue, niceCeil, seriesVar, toNumber, type ValueFormat } from '../format';

interface Series {
  key: string;
  label: string;
}

/** Legend is always present for two or more series; a single series is named by the title. */
function Legend({ series }: { series: Series[] }) {
  if (series.length < 2) return null;
  return (
    <div className="legend">
      {series.map((s, i) => (
        <span className="legend-item" key={s.key}>
          <i style={{ background: seriesVar(i) }} aria-hidden />
          {s.label}
        </span>
      ))}
    </div>
  );
}

interface TooltipState {
  x: number;
  y: number;
  title: string;
  rows: { label: string; value: string; colour: string }[];
}

function Tooltip({ state, width }: { state: TooltipState | null; width: number }) {
  if (!state) return null;
  // Flip the tooltip to the left of the cursor near the right edge so it never
  // gets clipped by the card.
  const flip = state.x > width - 170;
  return (
    <div
      className="tooltip"
      style={{
        left: flip ? undefined : state.x + 12,
        right: flip ? width - state.x + 12 : undefined,
        top: Math.max(0, state.y - 10),
      }}
      role="status"
    >
      <div className="t-title">{state.title}</div>
      {state.rows.map((r) => (
        <div className="t-row" key={r.label}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <i style={{ background: r.colour }} aria-hidden />
            {r.label}
          </span>
          <b>{r.value}</b>
        </div>
      ))}
    </div>
  );
}

/** Every chart ships a table-view twin — the WCAG-clean equivalent, and the
 *  required relief for series colours below 3:1 against a light surface. */
function TableView({
  points,
  series,
  xLabel,
  format,
}: {
  points: any[];
  series: Series[];
  xLabel: string;
  format: ValueFormat;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="toggle-table">
      <button className="ghost small" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? '▾ Hide data table' : '▸ Show data table'}
      </button>
      {open && (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="grid">
            <thead>
              <tr>
                <th>{xLabel}</th>
                {series.map((s) => (
                  <th className="right" key={s.key}>
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {points.map((p, i) => (
                <tr key={i}>
                  <td>{String(p.x)}</td>
                  {series.map((s) => (
                    <td className="right" key={s.key}>
                      {formatValue(p[s.key], format)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------- bar ------

export function BarChart({
  points,
  series,
  stacked,
  xLabel = 'Category',
  format = 'integer',
}: {
  points: any[];
  series: Series[];
  stacked?: boolean;
  xLabel?: string;
  format?: ValueFormat;
}) {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const W = 720;
  // Left gutter sized to the longest category label rather than fixed. A fixed
  // gutter is either too small (clipped consultant names) or wastes a third of
  // the card on whitespace when the labels are short.
  const longest = points.reduce((max, p) => Math.max(max, String(p.x ?? '').slice(0, 24).length), 0);
  const padL = Math.min(200, Math.max(64, longest * 5.2 + 14));
  const padR = 54;
  const padT = 8;
  const rowH = 26;
  const gap = 10;
  const H = padT + Math.max(points.length, 1) * (rowH + gap) + 30;
  const plotW = W - padL - padR;

  const scaleMax = useMemo(() => {
    const max = stacked
      ? Math.max(0, ...points.map((p) => series.reduce((a, s) => a + toNumber(p[s.key]), 0)))
      : Math.max(0, ...points.flatMap((p) => series.map((s) => toNumber(p[s.key]))));
    return niceCeil(max);
  }, [points, series, stacked]);

  if (!points.length || !series.length) {
    return <p className="note">No data for this chart.</p>;
  }

  const showTip = (event: React.MouseEvent, point: any) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      title: String(point.x),
      rows: series.map((s, i) => ({ label: s.label, value: formatValue(point[s.key], format), colour: seriesVar(i) })),
    });
  };

  const barH = stacked ? rowH - 10 : Math.max(5, (rowH - 8) / series.length - 2);

  return (
    <div className="chart-shell" ref={wrapRef}>
      <Legend series={series} />
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={`Bar chart of ${xLabel}`}>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            {/* Solid hairline gridlines — never dashed. */}
            <line x1={padL + f * plotW} y1={padT} x2={padL + f * plotW} y2={H - 30} stroke="var(--grid)" strokeWidth={1} />
            <text x={padL + f * plotW} y={H - 12} className="tick" textAnchor="middle">
              {formatValue(Math.round(scaleMax * f), format)}
            </text>
          </g>
        ))}
        <line x1={padL} y1={padT} x2={padL} y2={H - 30} stroke="var(--axis)" strokeWidth={1} />

        {points.map((point, pi) => {
          const yTop = padT + pi * (rowH + gap);
          let cursor = padL;
          return (
            <g
              key={pi}
              onMouseMove={(e) => showTip(e, point)}
              onMouseLeave={() => setTip(null)}
              tabIndex={0}
              onFocus={(e) => {
                const rect = wrapRef.current?.getBoundingClientRect();
                const box = (e.target as SVGGElement).getBoundingClientRect();
                if (!rect) return;
                // Keyboard focus shows the same information as hover.
                setTip({
                  x: box.left - rect.left + 160,
                  y: box.top - rect.top,
                  title: String(point.x),
                  rows: series.map((s, i) => ({
                    label: s.label,
                    value: formatValue(point[s.key], format),
                    colour: seriesVar(i),
                  })),
                });
              }}
              onBlur={() => setTip(null)}
            >
              {/* Full-row hit target, comfortably larger than the marks. */}
              <rect x={padL} y={yTop} width={plotW} height={rowH} fill="transparent" />
              <text x={padL - 10} y={yTop + rowH / 2 + 4} className="cat" textAnchor="end">
                {/* Same 24-char cut as the gutter calculation above and as the
                    print renderer, so screen and PDF truncate identically. */}
                {String(point.x).length > 24 ? `${String(point.x).slice(0, 23)}…` : String(point.x)}
              </text>

              {series.map((s, si) => {
                const value = toNumber(point[s.key]);
                const width = scaleMax > 0 ? (value / scaleMax) * plotW : 0;
                if (stacked) {
                  const x = cursor;
                  cursor += width;
                  if (width <= 0) return null;
                  return (
                    // 2px surface gap between segments rather than a border.
                    <rect key={s.key} x={x} y={yTop + 5} width={Math.max(1, width - 2)} height={barH} rx={3} fill={seriesVar(si)} />
                  );
                }
                const y = yTop + 4 + si * (barH + 2);
                return (
                  <g key={s.key}>
                    <rect x={padL} y={y} width={Math.max(0, width)} height={barH} rx={3} fill={seriesVar(si)} />
                    {/* Selective direct labels: only when few enough series to fit. */}
                    {series.length <= 2 && value > 0 && (
                      <text x={padL + width + 6} y={y + barH / 2 + 3.5} className="val">
                        {formatValue(value, format)}
                      </text>
                    )}
                  </g>
                );
              })}

              {stacked && (
                <text x={cursor + 6} y={yTop + rowH / 2 + 4} className="val">
                  {formatValue(
                    series.reduce((a, s) => a + toNumber(point[s.key]), 0),
                    format,
                  )}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Tooltip state={tip} width={wrapRef.current?.clientWidth ?? 720} />
      <TableView points={points} series={series} xLabel={xLabel} format={format} />
    </div>
  );
}

// ------------------------------------------------------------------ line ------

export function LineChart({
  points,
  series,
  xLabel = 'Period',
  format = 'integer',
}: {
  points: any[];
  series: Series[];
  xLabel?: string;
  format?: ValueFormat;
}) {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const W = 720;
  const H = 250;
  const padL = format === 'currency' ? 66 : 52;
  const padR = format === 'currency' ? 74 : 58;
  const padT = 14;
  const padB = 32;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const scaleMax = useMemo(
    () => niceCeil(Math.max(0, ...points.flatMap((p) => series.map((s) => toNumber(p[s.key]))))),
    [points, series],
  );

  if (points.length < 2 || !series.length) {
    return <p className="note">Not enough data points to draw a trend.</p>;
  }

  const xAt = (i: number) => padL + (i / (points.length - 1)) * plotW;
  const yAt = (v: number) => padT + plotH - (scaleMax > 0 ? (v / scaleMax) * plotH : 0);

  const onMove = (event: React.MouseEvent<SVGSVGElement>) => {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const wrapRect = wrapRef.current?.getBoundingClientRect();
    if (!wrapRect) return;
    // Map the pointer to the nearest data index rather than requiring a hit on
    // the marker itself.
    const relative = ((event.clientX - rect.left) / rect.width) * W;
    const ratio = Math.min(1, Math.max(0, (relative - padL) / plotW));
    const index = Math.round(ratio * (points.length - 1));
    setHoverIndex(index);
    setTip({
      x: event.clientX - wrapRect.left,
      y: event.clientY - wrapRect.top,
      title: String(points[index].x),
      rows: series.map((s, i) => ({ label: s.label, value: formatValue(points[index][s.key], format), colour: seriesVar(i) })),
    });
  };

  const tickEvery = Math.max(1, Math.ceil(points.length / 6));

  return (
    <div className="chart-shell" ref={wrapRef}>
      <Legend series={series} />
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        role="img"
        aria-label={`Trend chart over ${xLabel}`}
        onMouseMove={onMove}
        onMouseLeave={() => {
          setTip(null);
          setHoverIndex(null);
        }}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={padL} y1={padT + plotH - f * plotH} x2={padL + plotW} y2={padT + plotH - f * plotH} stroke="var(--grid)" strokeWidth={1} />
            <text x={padL - 8} y={padT + plotH - f * plotH + 3.5} className="tick" textAnchor="end">
              {formatValue(Math.round(scaleMax * f), format)}
            </text>
          </g>
        ))}
        <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="var(--axis)" strokeWidth={1} />

        {points.map((p, i) =>
          i % tickEvery === 0 || i === points.length - 1 ? (
            <text key={i} x={xAt(i)} y={H - 11} className="tick" textAnchor="middle">
              {String(p.x).slice(0, 10)}
            </text>
          ) : null,
        )}

        {hoverIndex !== null && (
          <line x1={xAt(hoverIndex)} y1={padT} x2={xAt(hoverIndex)} y2={padT + plotH} stroke="var(--axis)" strokeWidth={1} />
        )}

        {series.map((s, si) => {
          const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(toNumber(p[s.key])).toFixed(1)}`).join(' ');
          const lastValue = toNumber(points[points.length - 1][s.key]);
          return (
            <g key={s.key}>
              <path d={path} fill="none" stroke={seriesVar(si)} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {/* 2px surface ring on the markers instead of a border. */}
              <circle cx={xAt(0)} cy={yAt(toNumber(points[0][s.key]))} r={4} fill={seriesVar(si)} stroke="var(--surface)" strokeWidth={2} />
              <circle
                cx={xAt(points.length - 1)}
                cy={yAt(lastValue)}
                r={4}
                fill={seriesVar(si)}
                stroke="var(--surface)"
                strokeWidth={2}
              />
              {hoverIndex !== null && (
                <circle
                  cx={xAt(hoverIndex)}
                  cy={yAt(toNumber(points[hoverIndex][s.key]))}
                  r={5}
                  fill={seriesVar(si)}
                  stroke="var(--surface)"
                  strokeWidth={2}
                />
              )}
              {series.length <= 4 && (
                <text x={padL + plotW + 6} y={yAt(lastValue) + 3.5} className="end-label" fill={seriesVar(si)}>
                  {formatValue(lastValue, format)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <Tooltip state={tip} width={wrapRef.current?.clientWidth ?? 720} />
      <TableView points={points} series={series} xLabel={xLabel} format={format} />
    </div>
  );
}

// ---------------------------------------------------------------- funnel ------

/**
 * Ordered stages use an ordinal one-hue ramp, not categorical identity hues.
 *
 * Brand purple, light to dark. Validated as an ordinal ramp against the card
 * surface: monotone lightness, every adjacent step at least 0.06 L apart, and a
 * light end that clears the surface at 2.1:1 — a paler first step looks better
 * in isolation and then vanishes against white.
 */
const ORDINAL = ['#c9a6db', '#b78ecd', '#a473bd', '#9159ab', '#7a4494', '#623276', '#4a245c'];

export function Funnel({ stages }: { stages: { label: string; value: number; fromPrevious: number | null; fromTop: number | null }[] }) {
  if (!stages.length) return <p className="note">No funnel data.</p>;
  const top = stages[0]?.value ?? 0;
  return (
    <div>
      {stages.map((stage, i) => {
        const pct = top > 0 ? (stage.value / top) * 100 : 0;
        return (
          <div className="funnel-row" key={stage.label}>
            <div className="funnel-label">{stage.label}</div>
            <div
              className="funnel-track"
              role="img"
              aria-label={`${stage.label}: ${stage.value}${stage.fromPrevious !== null ? `, ${stage.fromPrevious}% of previous stage` : ''}`}
            >
              <div className="funnel-fill" style={{ width: `${Math.max(pct, 1.5)}%`, background: ORDINAL[Math.min(i, ORDINAL.length - 1)] }} />
            </div>
            <div className="funnel-value">{formatValue(stage.value, 'integer')}</div>
            <div className="funnel-rate">
              {i === 0 ? (
                <span style={{ color: 'var(--muted)' }}>baseline</span>
              ) : stage.fromPrevious === null ? (
                <span style={{ color: 'var(--muted)' }}>—</span>
              ) : (
                <>
                  {formatValue(stage.fromPrevious, 'percent')} <span style={{ color: 'var(--muted)' }}>of previous</span>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
